import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService.js';
import OTPInput from './OTPInput.jsx';
import PasswordStrengthIndicator from '../common/PasswordStrengthIndicator.jsx';


const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.sendOTP(formData.email, 'password_reset');
      setStep(2);
      setSuccess('Secure verification code sent to your email!');
    } catch (error) {
      setError(error.error || error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otp) => {
    setLoading(true);
    setError('');

    try {
      await authService.verifyOTP(otp);
      setStep(3);
    } catch (error) {
      setError(error.error || error.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await authService.resetPassword(formData.newPassword);
      setSuccess('Password reset successfully! You can now login with your new password.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.error || error.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <form onSubmit={handleSendOTP}>
            <div className="form-group secure">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Enter your registered email address"
              />
            </div>

            
            <button 
              type="submit" 
              className={`btn btn-primary ${loading ? 'loading' : ''}`}
              disabled={loading}
              style={{ width: '100%' }}
            >
              {loading ? (
                <>
                  <i className="fas fa-shield-alt"></i>
                  Sending Secure Code...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  Send Verification Code
                </>
              )}
            </button>
          </form>
        );

      case 2:
        return (
          <div>
            <div className="alert alert-info">
              <i className="fas fa-info-circle"></i>
              🔐 Verify Security Code .Enter the 6-digit code sent to {formData.email}
            </div>
           
           
            <OTPInput 
              onComplete={handleVerifyOTP}
              disabled={loading}
            />
            {loading && <div className="loading">
              
              Verifying secure code...
            </div>}
          </div>
        );

      case 3:
        return (
          <form onSubmit={handleResetPassword}>
            <div className="form-group secure">
              <label htmlFor="newPassword">New Password:</label>
              <div className="password-input-container">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Create a strong new password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={toggleNewPasswordVisibility}
                  disabled={loading}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              <PasswordStrengthIndicator password={formData.newPassword} />
            </div>

            <div className="form-group secure">
              <label htmlFor="confirmPassword">Confirm New Password:</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={toggleConfirmPasswordVisibility}
                  disabled={loading}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            

            <button 
              type="submit" 
              className={`btn btn-primary ${loading ? 'loading' : ''}`}
              disabled={loading}
              style={{ width: '100%' }}
            >
              {loading ? (
                <>
                  <i className="fas fa-shield-alt"></i>
                  Securing New Password...
                </>
              ) : (
                <>
                  <i className="fas fa-key"></i>
                  Reset Password
                </>
              )}
            </button>
          </form>
        );

      default:
        return null;
    }
  };
  return (
    <div className="auth-page">
      <div className="form-container auth-security-container">
        {/* Security Shield Indicator */}
       

        <div className="auth-header">
          <h2>🔐Password Reset</h2>
          <p>Step {step} of 3 - Reset your password securely</p>
        </div>

        {error && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}

        {renderStep()}

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <Link to="/login" className="auth-link secondary">
            <i className="fas fa-arrow-left"></i> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;